import { FormEvent, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../auth/store';
import { BrandHero } from '../brand/BrandHero';

/**
 * Two-step login flow:
 *
 *   step "credentials" → email + password.
 *     ↳ result "ok"            → navigate away.
 *     ↳ result "totp_required" → flip to step "second-factor", keep email/password in state.
 *     ↳ everything else        → show error, stay on credentials step.
 *
 *   step "second-factor" → either TOTP (6 digits) or recovery code (10 chars, XXXXX-XXXXX).
 *     ↳ submits the SAME login() call but with the second factor supplied.
 *
 * Keeping the two steps in one component (rather than two routes) means the email + password
 * never leave React state — no risk of someone navigating back to the credentials step and
 * resending the bare password as a GET-cached form.
 *
 * Visual layer: full-bleed obsidian-and-gold brand stage from `BrandHero`, with the form
 * rendered as a glass-morphism panel that sits over the hero on phones (stacked) and beside it
 * on desktop (split). Same component is reused on /signup so the two flows feel cohesive.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<'credentials' | 'second-factor'>('credentials');
  const [factorMode, setFactorMode] = useState<'totp' | 'recovery'>('totp');
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/';

  const submitCredentials = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const result = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    switch (result.status) {
      case 'ok':
        navigate(redirectTo, { replace: true });
        return;
      case 'totp_required':
        setStep('second-factor');
        return;
      case 'invalid_credentials':
        setError('Wrong email or password');
        return;
      case 'rate_limited':
        setError(
          `Too many attempts; try again in ${result.retryAfterSeconds ?? 60} seconds.`,
        );
        return;
      case 'totp_wrong':
        // Shouldn't happen on the credentials step (we didn't supply a code), but treat
        // defensively — the server's the authority.
        setStep('second-factor');
        setError('Two-factor required.');
        return;
      default:
        setError('Sign-in failed; check your connection.');
    }
  };

  const submitSecondFactor = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const second =
      factorMode === 'totp'
        ? { totp_code: totpCode.trim() }
        : { recovery_code: recoveryCode.trim() };
    const result = await login(email.trim().toLowerCase(), password, second);
    setSubmitting(false);
    switch (result.status) {
      case 'ok':
        navigate(redirectTo, { replace: true });
        return;
      case 'totp_wrong':
        setError(factorMode === 'totp' ? 'Wrong code; try again.' : 'Wrong recovery code.');
        return;
      case 'invalid_credentials':
        // Password was correct the first time but isn't any more — token expired or session
        // race. Reset.
        setStep('credentials');
        setPassword('');
        setError('Session expired; please re-enter your password.');
        return;
      case 'rate_limited':
        setError(
          `Too many attempts; try again in ${result.retryAfterSeconds ?? 60} seconds.`,
        );
        return;
      default:
        setError('Verification failed; check your connection.');
    }
  };

  return (
    <main className="brand-stage">
      <BrandHero
        subtitle={
          step === 'credentials' ? 'Sign in to your secure session' : `Verify · ${email}`
        }
      />

      <aside className="brand-panel">
        <div className="brand-panel-inner">
          <h2 className="brand-panel-title">
            {step === 'credentials' ? 'Sign in' : 'Two-factor verification'}
          </h2>

          {step === 'credentials' && (
            <form onSubmit={submitCredentials} className="space-y-4" autoComplete="on">
              <Field label="Email">
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-luxe text-sm"
                />
              </Field>
              <Field label="Password">
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-luxe text-sm"
                />
              </Field>
              {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="btn-gold w-full py-3 disabled:opacity-50 tracking-[0.2em] uppercase text-sm"
              >
                {submitting ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          )}

          {step === 'second-factor' && (
            <form onSubmit={submitSecondFactor} className="space-y-4" autoComplete="off">
              {factorMode === 'totp' ? (
                <Field label="Authenticator code">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    required
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    className="input-luxe text-center text-2xl font-mono tracking-[0.4em]"
                  />
                </Field>
              ) : (
                <Field label="Recovery code">
                  <input
                    type="text"
                    autoComplete="off"
                    required
                    placeholder="XXXXX-XXXXX"
                    value={recoveryCode}
                    onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                    className="input-luxe text-center font-mono tracking-wider"
                  />
                </Field>
              )}
              {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="btn-gold w-full py-3 disabled:opacity-50 tracking-[0.2em] uppercase text-sm"
              >
                {submitting ? 'Verifying…' : 'Verify'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setFactorMode((m) => (m === 'totp' ? 'recovery' : 'totp'));
                  setTotpCode('');
                  setRecoveryCode('');
                  setError(null);
                }}
                className="w-full text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition"
              >
                {factorMode === 'totp' ? 'Use a recovery code instead' : 'Use my authenticator app'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStep('credentials');
                  setError(null);
                  setTotpCode('');
                  setRecoveryCode('');
                }}
                className="w-full text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition"
              >
                ← back
              </button>
            </form>
          )}

          {step === 'credentials' && (
            <>
              <p className="mt-6 text-center text-sm text-[var(--color-text-muted)]">
                New here?{' '}
                <Link to="/signup" className="text-[var(--color-accent)] hover:underline">
                  Create an account
                </Link>
              </p>
              <p className="mt-2 text-center text-xs text-[var(--color-text-muted)]">
                <Link to="/" className="hover:underline">
                  ← back to quick connect
                </Link>
              </p>

              {/* Secondary CTA: download the host-side program. Tasteful glass card; uses the same
                  accent + border tokens as the rest of the panel so it feels native, not bolted-on.
                  Tied to a single static file in `web/public/` so it stays available even to users
                  who aren't signed in. */}
              <div className="mt-7 pt-5 border-t border-[var(--color-border)]/50">
                <a
                  href="/Linkora-host.zip"
                  download
                  className="group flex items-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]/40 px-3.5 py-3 transition hover:border-[var(--color-accent)] hover:bg-[var(--color-surface)]/80 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/40"
                >
                  <span className="flex-none w-10 h-10 rounded-lg bg-[var(--color-accent)]/12 text-[var(--color-accent)] grid place-items-center text-xl ring-1 ring-[var(--color-accent)]/25 transition group-hover:bg-[var(--color-accent)]/20">
                    ⤓
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[10px] uppercase tracking-[0.28em] text-[var(--color-text-muted)]">
                      Host on this PC
                    </span>
                    <span className="block text-sm font-medium text-[var(--color-text)] truncate">
                      Linkora для Windows
                    </span>
                  </span>
                  <span className="flex-none text-[10px] uppercase tracking-[0.22em] text-[var(--color-text-muted)] group-hover:text-[var(--color-accent)] transition">
                    3.8&nbsp;МБ&nbsp;·&nbsp;.zip
                  </span>
                </a>
              </div>
            </>
          )}
        </div>
      </aside>
    </main>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.28em] text-[var(--color-text-muted)] mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
